
import {defineComponent, nextTick} from "vue";
import {Exception} from "@/api/interfaces";
import {
  CodeDescriptionRestDto,
  ColorRestDto,
  ContentPageRestDto,
  ContentPageRowRestDto,
  PageLibraryOverviewRestDto,
  WysiwygContentPageRestDto,
  WysiwygContentPageRowRestDto,
} from "@/api/pharma-cms-content-page/models";
import ContentPageRowDetail from "@/components/layouts/content-pages/wysiwyg/ContentPageRowDetail.vue";
import ContentPageColumnDetail from "@/components/layouts/content-pages/wysiwyg/ContentPageColumnDetail.vue";
import {PredefinedContentPageRowEnum} from "@/models/enum/PredefinedContentPageRowEnum";
import ContentPageAddColumn from "@/components/layouts/content-pages/wysiwyg/ContentPageAddColumn.vue";
import {ContentPageRestService} from "@/services/rest/cms-content-page/ContentPageRestService";
import {ContentPageRowRestService} from "@/services/rest/cms-content-page/ContentPageRowRestService";
import {ContentBlockInRowRestService} from "@/services/rest/cms-content-page/ContentBlockInRowRestService";
import {ContentPageLibraryRestService} from "@/services/rest/cms-content-page/ContentPageLibraryRestService";
import {WysiwygContentPageRestDtoModel} from "@/models/api/pharma-cms-content-page/WysiwygContentPageRestDtoModel";
import {default as Offcanvas} from 'bootstrap/js/dist/offcanvas';
import {default as Modal} from "bootstrap/js/dist/modal";
import {Container, Draggable} from "vue-dndrop";
import ConfirmModal from "@/components/UI/ConfirmModal.vue";
import {createConfirmDialog} from "vuejs-confirm-dialog";
import {useToast} from "vue-toastification";
import {Popover, Tooltip} from "bootstrap";
import BaseTitle from "@/components/UI/BaseTitle.vue"
import {generateContentPagePreviewUrl, generateContentPagePriceCardPreviewUrl} from "@/helpers/functions/route";
import {AutoSaveFeedbackStatusEnum} from "@/models/enum/AutoSaveFeedbackStatusEnum";
import ContentPagePageSettings from "@/components/layouts/content-pages/wysiwyg/ContentPagePageSettings.vue";
import ContentPagePageAbstract from "@/components/layouts/content-pages/wysiwyg/ContentPagePageAbstract.vue";
import {EnumClassNameCmsEnum} from "@/models/enum/EnumClassNameCmsEnum";
import {StatusEnum} from "@/api/enums/status-enum";
import {MoveActionEnum} from "@/models/enum/MoveActionEnum";
import {ContentCustomerRestService} from "@/services/rest/content-customer/ContentCustomerRestService";
import {ColorSchemeRestDtoModel} from "@/models/api/pharma-cms-color-scheme/ColorSchemeRestDtoModel";
import ContentPageColumn from "@/components/layouts/content-pages/wysiwyg/ContentPageColumn.vue";
import {AuthContext} from "@/context/AuthContext";
import MetadataIcons from "@/components/layouts/content-pages/wysiwyg/misc/MetadataIcons.vue";
import {ContentPageRowClipboardModel} from "@/models/clipboard/ContentPageRowClipboardModel";
import ContentPageCopy from "@/components/layouts/content-pages/wysiwyg/ContentPageCopy.vue";
import {mapActions as mapActionsP, mapState} from "pinia";
import {useCustomerStore} from "@/stores/CustomerStore";
import {useClipboardStore} from "@/stores/ClipboardStore";
import BaseOffcanvas from "@/components/UI/Bootstrap/Offcanvas/BaseOffcanvas.vue";
import BaseOffcanvasButtonClose from "@/components/UI/Bootstrap/Offcanvas/BaseOffcanvasButtonClose.vue";
import BaseOffcanvasButtonOpen from "@/components/UI/Bootstrap/Offcanvas/BaseOffcanvasButtonOpen.vue";
import ContentPageRowVisibilityRule from "@/components/layouts/content-pages/wysiwyg/ContentPageRowVisibilityRule.vue";
import {AbilityContext} from "@/context/AbilityContext";
import ContentPageColumnVisibilityRule
  from "@/components/layouts/content-pages/wysiwyg/ContentPageColumnVisibilityRule.vue";
import {getDescriptionVisibilityRuleStatus} from "@/helpers/functions/visibility-rules";
import SnippetMetaData from "@/components/layouts/content-pages/snippets/SnippetMetaData.vue";
import {SnippetRestService} from "@/services/rest/cms-snippet/SnippetRestService";
import {PageDetailModeEnum} from "@/models/enum/PageDetailModeEnum";
import {EnumRestService} from "@/services/rest/enum/EnumRestService";
import {DpModuleEnum} from "@/api/enums/dp-module-enum";
import {PageTypeEnum} from "@/api/enums/page-type-enum";

const __default__ = defineComponent({
  name: "CpsDetailWysiwyg",
  components: {
    SnippetMetaData,
    ContentPageColumnVisibilityRule,
    ContentPageRowVisibilityRule,
    BaseOffcanvasButtonOpen,
    BaseOffcanvasButtonClose,
    BaseOffcanvas,
    ContentPageCopy,
    MetadataIcons,
    ContentPageColumn,
    ContentPagePageAbstract,
    ContentPagePageSettings,
    ContentPageAddColumn,
    ContentPageColumnDetail,
    ContentPageRowDetail,
    Container,
    Draggable,
    BaseTitle,
  },
  props: {
    id: {
      type: String,
    },
    mode: {
      type: String,
      required: true,
    },
    libCode: {
      type: String,
      required: false,
    },
    isHomepage: {
      type: Boolean,
      required: false,
      default: false
    },
    isNamedPage: {
      type: Boolean,
      required: false,
      default: false
    },
    namedPageTypeCode: {
      type: String,
      required: false,
    },
    backRoute: {
      type: String,
      required: true,
    }
  },
  data() {
    return {
      exceptions: [] as Exception[],
      isReady: false as boolean,
      pageType: null as string | null,
      colorScheme: ColorSchemeRestDtoModel.createWithDefaults() as ColorSchemeRestDtoModel,

      contentPage: WysiwygContentPageRestDtoModel.createWithDefaults() as WysiwygContentPageRestDto,
      pageTypeEnums: [] as CodeDescriptionRestDto[],
      contentPageLibraries: [] as PageLibraryOverviewRestDto[],

      enums: {
        predefinedContentPageRowEnum: PredefinedContentPageRowEnum,
        autoSaveFeedbackStatus: AutoSaveFeedbackStatusEnum,
      },

      activeRowId: null as string | null,
      activeColumnId: null as string | null,
      activeColumnRowId: null as string | null,
      hasHomepage: false as boolean,
      hasNamedPage: false as boolean,

      toaster: useToast(),

      editRowPopover: null as Popover | null,
      editColumnPopover: null as Popover | null,

      addRowOffcanvas: null as Offcanvas | null,
      editRowOffcanvas: null as Offcanvas | null,
      editRowVisibilityRuleOffcanvas: null as Offcanvas | null,

      addColumnOffcanvas: null as Offcanvas | null,
      editColumnOffcanvas: null as Offcanvas | null,
      editColumnVisibilityRuleOffcanvas: null as Offcanvas | null,

      copyContentPageModal: null as Modal | null,

      tooltips: [] as Tooltip[],
      enumContentStatus: [] as CodeDescriptionRestDto[],
    };
  },
  mounted() {
    this.initialize();
  },
  beforeUnmount() {
    this.editRowPopover?.dispose();
    this.editColumnPopover?.dispose();
    this.tooltips.forEach(tt => {
      tt.dispose();
    });
  },
  unmounted() {
    this.copyContentPageModal?.dispose();
  },
  methods: {
    getDescriptionVisibilityRuleStatus,
    ...mapActionsP(useClipboardStore, ["copyToClipboardContentPageRowIds", "pasteClipboardContentPageRowIds"]),
    async initialize(): Promise<void> {
      this.addRowOffcanvas = new Offcanvas('#offcanvas-add-row');
      this.editRowOffcanvas = new Offcanvas('#offcanvas-edit-row');
      this.editRowVisibilityRuleOffcanvas = new Offcanvas('#offcanvas-edit-row-visibility-rule');

      this.addColumnOffcanvas = new Offcanvas('#offcanvas-add-column');
      this.editColumnOffcanvas = new Offcanvas('#offcanvas-edit-column');
      this.editColumnVisibilityRuleOffcanvas = new Offcanvas('#offcanvas-edit-column-visibility-rule');

      if (this.backRoute !== 'snippets' && this.backRoute !== 'popup-detail') {
        this.copyContentPageModal = new Modal('#copy-content-page-modal');
      }
      this.enumContentStatus = await EnumRestService.getInstance().getEnumValuesByClassName(EnumClassNameCmsEnum.CONTENT_STATUS, DpModuleEnum.CMS);

      if (this.isLibrary) {
        // The library list used to live in a Vuex module that only one other screen ever filled, so
        // reaching this screen by deep link or refresh left it empty and the title computed threw.
        // This screen now fetches what it needs itself.
        try {
          this.contentPageLibraries = await ContentPageLibraryRestService.getInstance()
            .findContentPageLibrariesForContentProvider();
        } catch (exceptions: any) {
          this.exceptions = exceptions;
        }
      }

      await this.reloadContent();
    },

    priceCardPreviewUrl(size: string) {
      return generateContentPagePriceCardPreviewUrl(this.contentPage.id, size);
    },

    async moveColumn(moveAction: MoveActionEnum): Promise<void> {
      await ContentBlockInRowRestService.getInstance()
        .moveContentBlockInRow(this.activeColumnRowId as string, this.activeColumnId as string, moveAction);

      this.editColumnPopover?.hide();
      this.hideTooltips();
      this.toaster.success(this.$t('cms.successfullyMovedColumn'));

      await this.reloadContent();
    },
    async duplicateColumn(): Promise<void> {
      await ContentBlockInRowRestService.getInstance()
        .copyContentBlockInRow(this.activeColumnRowId as string, this.activeColumnId as string);

      this.editColumnPopover?.hide();
      this.hideTooltips();
      this.toaster.success(this.$t('cms.successfullyDuplicatedColumn'));

      await this.reloadContent();
    },
    confirmDeleteColumn(): void {
      const {reveal, onConfirm} = createConfirmDialog(ConfirmModal, {
        title: this.$t('cms.deleteColumn'),
        body: this.$t('cms.confirmDeleteColumn'),
        confirmButtonText: this.$t('delete'),
        confirmButtonClass: 'btn-danger',
      });

      onConfirm(async () => {
        await ContentBlockInRowRestService.getInstance()
          .deleteContentBlockInRow(this.activeColumnRowId as string, this.activeColumnId as string);

        this.editColumnPopover?.hide();
        this.hideTooltips();
        this.toaster.success(this.$t('cms.successfullyDeletedColumn'));

        await this.reloadContent();
      });

      reveal();
    },
    async moveRow(moveAction: MoveActionEnum, rowId?: string): Promise<void> {
      await ContentPageRowRestService.getInstance()
        .moveContentPageRow(this.contentPage.id, (rowId ?? this.activeRowId) as string, moveAction);

      this.editRowPopover?.hide();
      this.hideTooltips();
      this.toaster.success(this.$t('cms.successfullyMovedRow'));

      await this.reloadContent();
    },
    copyRow(): void {
      this.copyToClipboardContentPageRowIds(this.activeRowId, this.contentPage.id);

      this.editRowPopover?.hide();
      this.hideTooltips();
      this.toaster.info(this.$t('cms.successfullyCopiedRow'));
    },
    async pasteRow(): Promise<void> {
      await this.pasteClipboardContentPageRowIds(this.contentPage.id);

      this.toaster.success(this.$t('cms.successfullyPastedRow'));

      await this.reloadContent();
      window.scrollTo(0, document.body.scrollHeight);
    },
    async duplicateRow(): Promise<void> {
      await ContentPageRowRestService.getInstance()
        .copyContentPageRow(this.contentPage.id, this.activeRowId as string);

      this.editRowPopover?.hide();
      this.hideTooltips();
      this.toaster.success(this.$t('cms.successfullyDuplicatedRow'));

      await this.reloadContent();
    },
    confirmDeleteRow() {
      const {reveal, onConfirm} = createConfirmDialog(ConfirmModal, {
        title: this.$t('cms.deleteRow'),
        body: this.$t('cms.confirmDeleteRow'),
        confirmButtonText: this.$t('delete'),
        confirmButtonClass: 'btn-danger',
      });

      onConfirm(async () => {
        await ContentPageRowRestService.getInstance()
          .deleteContentPageRow(this.contentPage.id, this.activeRowId as string);

        this.editRowPopover?.hide();
        this.hideTooltips();
        this.toaster.success(this.$t('cms.successfullyDeletedRow'));

        await this.reloadContent();
      });

      reveal();
    },

    async onDrop(dropResult: any): Promise<void> {
      if (dropResult.removedIndex === dropResult.addedIndex) {
        // unchanged positions
        return;
      }

      const rowId = dropResult.element.getAttribute('data-row-id');

      console.log('dropResult', dropResult);
      console.log('rowId', rowId);

      let rowIndex = null;

      const row = this.contentPage.rows.find((row: WysiwygContentPageRowRestDto, index: number) => {
        if (row.id === rowId) {
          rowIndex = index;
          return true;
        }

        return false;
      }) as WysiwygContentPageRowRestDto;

      console.log('rowIndex', rowIndex);

      console.log('row', row);

      const column = row.columns[dropResult.removedIndex];
      const columnId = column.id;

      if (rowIndex !== null) {
        this.contentPage.rows[rowIndex].columns = this.applyDrag(row.columns, dropResult, column);
      }

      let moveAction = null;

      if (dropResult.addedIndex > dropResult.removedIndex) {
        moveAction = MoveActionEnum.DOWN;
      } else {
        moveAction = MoveActionEnum.UP;
      }

      const payload = {
        row_id: row.id,
        cb_in_row_id: columnId,
        move_action: moveAction,
      };

      const noOfCalls = Math.abs(dropResult.addedIndex - dropResult.removedIndex);

      for (let i = 0; i < noOfCalls; i++) {
        await ContentBlockInRowRestService.getInstance()
          .moveContentBlockInRow(payload.row_id, payload.cb_in_row_id, payload.move_action);
        this.toaster.success(this.$t('cms.successfullyMovedColumn'));
      }

      this.editColumnPopover?.update();

      /*let before = null;
      let after = null;

      if (dropResult.addedIndex > dropResult.removedIndex) {
        after = row.columns[dropResult.addedIndex].id;
      } else {
        before = row.columns[dropResult.addedIndex].id;
      }

      const payload = {
        rowId: rowId,
        columnId: columnId,
        position: {
          before_id: before,
          after_id: after,
        },
      };

      this.moveColumnToPosition(payload);*/
    },
    applyDrag(arr: any, dragResult: any, itemToAdd: any) {
      const {removedIndex, addedIndex} = dragResult;
      if (removedIndex === null && addedIndex === null) {
        return arr;
      }

      const result = [...arr];

      console.log('removedIndex', removedIndex);
      console.log('addedIndex', addedIndex);

      if (removedIndex !== null) {
        result.splice(removedIndex, 1);
      }

      if (addedIndex !== null) {
        result.splice(addedIndex, 0, itemToAdd);
      }

      return result;
    },
    activateRow(rowId: string): void {
      this.editColumnPopover?.hide();

      this.activeRowId = rowId;

      this.editRowPopover?.dispose();

      const content = this.$refs.editRowPopoverContent;

      this.editRowPopover = new Popover('#edit-row-' + rowId, {
        content: content,
        html: true,
        placement: 'left',
        trigger: 'manual',
        customClass: 'wysiwyg-popover',
      } as Partial<Popover.Options>);

      this.editRowPopover.show();
    },
    /**
     * The offcanvas forms below are reloaded on every open, never only when the element behind them
     * changes. Reopening the same row or column is how a user cancels: they close the offcanvas on
     * a form they could not save and open it again to start over, and that only works if what was
     * typed is thrown away and the stored state is fetched again.
     */
    async openEditRow(): Promise<void> {
      this.editRowPopover?.hide();
      this.hideTooltips();
      await (this.$refs.rowDetail as { reload(): Promise<void> } | undefined)?.reload();
    },
    async openEditRowVisibilityRule(): Promise<void> {
      this.editRowPopover?.hide();
      this.hideTooltips();
      await (this.$refs.rowVisibilityRule as { reload(): Promise<void> } | undefined)?.reload();
    },
    openAddColumn(): void {
      // Clear whatever was selected the previous time this offcanvas was open.
      (this.$refs.addColumn as { reset(): void } | undefined)?.reset();
      this.addColumnOffcanvas?.show();
      this.editRowPopover?.hide();
      this.hideTooltips();
    },
    openAddColumnFromRow(rowId: string): void {
      this.activeRowId = rowId;
      this.openAddColumn();
    },
    async openEditColumn(): Promise<void> {
      this.hideTooltips();
      await (this.$refs.columnDetail as { reload(): Promise<void> } | undefined)?.reload();
    },
    async openEditColumnVisibilityRule(): Promise<void> {
      this.hideTooltips();
      await (this.$refs.columnVisibilityRule as { reload(): Promise<void> } | undefined)?.reload();
    },
    openPageSettings(): void {
      (this.$refs.pageSettings as { reset(): void } | undefined)?.reset();
    },
    async openSnippetSettings(): Promise<void> {
      await (this.$refs.snippetMetaData as { reload(): Promise<void> } | undefined)?.reload();
    },
    async openPageAbstract(): Promise<void> {
      if (!this.contentPage.cp?.overview_cb_id) {
        await this.reloadContent();
      }
      await (this.$refs.pageAbstract as { reload(): Promise<void> } | undefined)?.reload();
    },
    activateColumn(columnId: string, rowId: string): void {
      this.editRowPopover?.hide();

      this.activeColumnId = columnId;
      this.activeColumnRowId = rowId;

      this.editColumnPopover?.dispose();

      const content = this.$refs.editColumnPopoverContent;

      this.editColumnPopover = new Popover('#column-' + columnId, {
        content: content,
        html: true,
        placement: 'top',
        trigger: 'manual',
        customClass: 'wysiwyg-popover',
      } as Partial<Popover.Options>);

      this.editColumnPopover.show();
    },
    async createHomepagePressed(): Promise<void> {
      this.isReady = false;
      this.exceptions = [];

      try {
        await ContentPageRestService.getInstance().createHomePage();
        await this.reloadContent();
      } catch (exceptions: any) {
        this.exceptions = exceptions;
      }

      this.isReady = true;
    },
    async createNamedPagePressed(): Promise<void> {
      this.isReady = false;
      this.exceptions = [];

      try {
        await ContentPageRestService.getInstance().createNamedPage(this.namedPageTypeCode as string);
        await this.reloadContent();
      } catch (exceptions: any) {
        this.exceptions = exceptions;
      }

      this.isReady = true;
    },
    async reloadContent(): Promise<void> {
      this.isReady = false;
      this.exceptions = [];

      try {
        this.pageTypeEnums = await ContentPageRestService.getInstance().findContentPageTypes(this.isLibrary);

        if (this.isHomepage) {
          const homePage = await ContentPageRestService.getInstance().getHomePageWysiwyg();
          this.hasHomepage = homePage !== null;
          this.contentPage = homePage ?? WysiwygContentPageRestDtoModel.createWithDefaults();
        } else if (this.isNamedPage) {
          const namedPage = await ContentPageRestService.getInstance().getNamedPageWysiwyg(this.namedPageTypeCode as string);
          this.hasNamedPage = namedPage !== null;
          this.contentPage = namedPage ?? WysiwygContentPageRestDtoModel.createWithDefaults();
        } else {
          if (this.mode === PageDetailModeEnum.NEW) {
            this.contentPage = WysiwygContentPageRestDtoModel.createWithDefaults();
            this.pageType = (this.isLibrary) ? (this.libCode ?? null) : this.pageTypeEnums[0].code;
          } else {
            this.contentPage = await ContentPageRestService.getInstance().getContentPageWysiwyg(this.id as string);
            if (this.contentPage.type) {
              this.pageType = this.contentPage.type.code;
            }
          }
        }

        if (this.isLibrary) {
          this.colorScheme = ColorSchemeRestDtoModel.createWithDefaults();
        } else {
          this.colorScheme = new ColorSchemeRestDtoModel(await ContentCustomerRestService.getInstance().findCustomerColorScheme());
        }
      } catch (exceptions: any) {
        this.exceptions = exceptions;
      }

      this.isReady = true;

      await nextTick();
      this.initializeTooltips();
    },
    initializeTooltips(): void {
      document.querySelectorAll('#cps-detail-wysiwyg [data-bs-toggle="tooltip"]').forEach(el => {
        const tooltip = new Tooltip(el as Element);
        this.tooltips.push(tooltip);
      });
    },
    hideTooltips(): void {
      this.tooltips.forEach(tt => {
        tt.hide();
      });
    },
    async addRow(type: PredefinedContentPageRowEnum): Promise<void> {
      switch (type) {
        case PredefinedContentPageRowEnum.EMPTY:
          await ContentPageRowRestService.getInstance().createContentPageRow(this.contentPage.id);
          break;
      }

      this.toaster.success(this.$t('cms.successfullyAddedRow'));

      await this.reloadContent();

      window.scrollTo(0, document.body.scrollHeight);
    },

    onContentPageAddColumnSubmit(): void {
      this.addColumnOffcanvas?.hide();

      this.toaster.success(this.$t('cms.successfullyAddedColumn'));

      this.reloadContent();
    },

    getColumnWidthCode(columnWidthEnum: string): string {
      return 'col col-' + columnWidthEnum.split('_')[1];
    },
    getHeightable(heightableEnum: string): string {
      return 'dp-h-' + heightableEnum.split('_')[1];
    },
    getRowClasses(row: ContentPageRowRestDto): string {
      return 'dndrop-container horizontal row'
        + ' ' + this.getHorizontalAlignment(row.items_horizontal_alignment_code)
        + ' ' + this.getVerticalAlignment(row.items_vertical_alignment_code)
        + ' ' + this.getHorizontalGutter(row.horizontal_gutter_code);
    },
    getHorizontalAlignment(horizontalAlignmentEnum: string): string {
      return 'justify-content-' + horizontalAlignmentEnum.toLowerCase();
    },
    getVerticalAlignment(verticalAlignmentEnum: string): string {
      if (verticalAlignmentEnum === 'MIDDLE') {
        verticalAlignmentEnum = 'center';
      }

      return 'align-items-' + verticalAlignmentEnum.toLowerCase();
    },
    getHorizontalGutter(horizontalGutterEnum: string): string {
      return 'gx-' + horizontalGutterEnum.replace('G_', '');
    },
    getBackgroundClasses(backgroundColorCodeEnum: string, backgroundColor: ColorRestDto): string {
      if (backgroundColor) {
        return '';
      }
      return 'dp-bg-' + backgroundColorCodeEnum.toLowerCase().replaceAll('_', '-');
    },
    getBackgroundStyle(backgroundColor: ColorRestDto): any {
      if (!backgroundColor) {
        return null;
      }
      return {
        backgroundColor: `rgba(${backgroundColor.rgb.red}, ${backgroundColor.rgb.green}, ${backgroundColor.rgb.blue}, ${backgroundColor.opacity})`
      };
    },
    async setStatusCode(status_code: StatusEnum): Promise<void> {
      this.exceptions = [];
      let initialStatusCode;

      try {
        if (this.contentPage?.cp?.status_code) {
          initialStatusCode = this.contentPage.cp.status_code;
          this.contentPage.cp.status_code = status_code;
        }

        await ContentPageRestService.getInstance()
          .updateContentPage(this.contentPage.cp as ContentPageRestDto, this.contentPage.id);
        if (this.backRoute === 'snippets') {
          SnippetRestService.getInstance().snippetsChanged();
        }

        this.toaster.success(this.$t('cms.successfullyUpdatedStatus'));
      } catch (exceptions: any) {
        this.exceptions = exceptions;

        if (this.contentPage?.cp?.status_code) {
          this.contentPage.cp.status_code = initialStatusCode;
        }
      }
    }
  },
  computed: {
    PageDetailModeEnum() {
      return PageDetailModeEnum
    },
    StatusEnum() {
      return StatusEnum
    },
    AbilityContext() {
      return AbilityContext
    },
    AuthContext() {
      return AuthContext
    },
    ...mapState(useClipboardStore, ["getClipboardContentPageRowIds"]),
    ...mapState(useCustomerStore, ["getCustomers"]),
    previewUrl(): string {
      return generateContentPagePreviewUrl(this.contentPage.id);
    },
    /**
     * content_id is not part of the row/column payload the detail offcanvas loads, so the page hands
     * it down from the wysiwyg tree it already has.
     */
    activeRowContentId(): string {
      return this.contentPage.rows
        .find((row: WysiwygContentPageRowRestDto) => row.id === this.activeRowId)?.content_id ?? '';
    },
    activeColumnContentId(): string {
      return this.contentPage.rows
        .find((row: WysiwygContentPageRowRestDto) => row.id === this.activeColumnRowId)
        ?.columns
        ?.find((column) => column.id === this.activeColumnId)?.content_id ?? '';
    },
    contentPageRowClipboard(): ContentPageRowClipboardModel {
      return this.getClipboardContentPageRowIds;
    },
    /**
     * The library this page belongs to, or undefined when it is not a library page or the list has
     * not been fetched yet. Null safe on purpose: the previous store getter dereferenced the result
     * of find() straight away, which threw and took the whole screen down whenever the list was empty.
     */
    contentPageLibrary(): PageLibraryOverviewRestDto | undefined {
      if (!this.isLibrary) {
        return undefined;
      }

      return this.contentPageLibraries
        .find((library: PageLibraryOverviewRestDto) => library.lib_code === this.libCode);
    },
    title(): string {
      if (this.isHomepage) {
        return this.$t('homepage');
      }
      if (this.isLibrary) {
        const libraryName = this.contentPageLibrary?.lib_name ?? this.libCode;

        return `${this.$t('websiteContent')} ${this.$t('library').toLowerCase()} (${libraryName})`;
      }
      if (this.backRoute === 'popup-detail') {
        return this.$t('popupPage');
      }

      return this.contentPage.cp?.name ?? this.$t('websiteContent');
    },
    libraryType(): string | undefined {
      return this.contentPageLibrary?.page_type_code;
    },
    returnRoute(): Object {
      if (this.isHomepage) {
        return {name: 'home'};
      }
      if (this.isNamedPage) {
        return {name: 'home'};
      }
      if (this.isLibrary) {
        return {name: 'libraries-content-pages-overview', params: {lib_code: this.libCode}};
      }
      if (this.backRoute === 'popup-detail') {
        return {
          name: 'popup-detail',
          params: {
            id: this.$route.params.popupId
          }
        };
      }

      return {name: this.backRoute};
    },
    isPriceCardPage(): boolean {
      return this.backRoute === 'price-cards';
    },
    isDpUser(): boolean {
      return AuthContext.isDpUser();
    },
    isLibrary(): boolean {
      return !!this.libCode;
    },
    /**
     * The homepage and the named pages are copied by their page type, since a customer has at most one of each.
     */
    copyPageTypeCode(): string | null {
      if (this.isHomepage) {
        return PageTypeEnum.HOME_PAGE_CONTENT;
      }
      if (this.isNamedPage) {
        return this.namedPageTypeCode as string;
      }

      return null;
    },
    MoveActionEnum() {
      return MoveActionEnum
    },
  },
});

import { useCssVars as _useCssVars } from 'vue'
const __injectCSSVars__ = () => {
_useCssVars(_ctx => ({
  "e78282e4": (_ctx.colorScheme?.primary_color),
  "6f379c92": (_ctx.colorScheme?.primary_lighter_color),
  "165f3c36": (_ctx.colorScheme?.primary_light_color),
  "0fea0b00": (_ctx.colorScheme?.secondary_color),
  "3d49c684": (_ctx.colorScheme?.secondary_lighter_color),
  "68e86057": (_ctx.colorScheme?.secondary_light_color)
}))}
const __setup__ = __default__.setup
__default__.setup = __setup__
  ? (props, ctx) => { __injectCSSVars__();return __setup__(props, ctx) }
  : __injectCSSVars__

export default __default__